
import dayjs, { Dayjs } from "dayjs";
import Vue from "vue";
import S3Image from "@/components/aws/S3Image.vue";
import FilterChipButton from "@/components/vuetify/FilterChipButton.vue";
import {
  ProductDetailTrendEntity,
  ProductDetailBasicInfoEntity,
  ProductDetailSalesDigestRateChartEntity,
  AlertMaster,
  SkuDetailTrendEntity,
  Sku,
  Color,
  Store,
  Size,
  ProductDetailSalesDigestRateChartData,
  Warehouse,
  DetailTrendItemEntity,
  GetDetailImagesEntity,
  ProductDetailActionInfoEntity,
  CustomActionMaster,
  Action,
  CustomActionDetail,
  AdminUser,
  StockActionDetail,
  ActionType,
  OrderedActionInfoEntity,
  StockActionInfoEntity,
  SkuActionData,
  AlertWithMaster,
  SkuFilterInfoEntity,
  SkuColorSizeDetailTrendEntity,
  ColorSizeDetailTrendItemEntity,
  FilterInfoDataEntity,
} from "@/api/entities";
import {
  AlertDetail,
  ColorDetail,
  ProductTrendTableHeader,
  SizeDetail,
  User,
  AdminUserDetail,
  SearchEntriesInProductDetailTrend,
} from "@/store/model";
import { ActionCreateDto, CreateActionDto } from "@/api/dto";
import ConfirmationDialog from "@/components/vuetify/ConfirmationDialog.vue";
import { Chart } from "highcharts-vue";
import { Options, TooltipOptions } from "highcharts";
import RadialApiClient from "@/api/RadialApiClient";
import * as Highcharts from "highcharts";
import { Constant } from "@/store/constant";
import Loading from "@/components/vuetify/Loading.vue";
import store from "@/store";
import mixin from "@/mixin/mixin";
import ElementDatePicker from "@/components/element/DatePicker.vue";

export interface ProductAlert {
  id: string;
  alertId: string;
  alertMasterId: number;
  userDeleted: boolean;
}

export interface SkuTrendTableTabs {
  colorId: string;
  sizes: SkuSizeTrendTableData[];
}

export interface SkuSizeTrendTableData {
  skuId: string;
  sizeId: string;
  skuAlerts: ProductAlert[];
}

interface SizeRow {
  colorId: string;
  sizeId: string;
  sizeName?: string;
  janCode: string | null;
  salesCounts: number[];
  salesPrices: number[];
  stockCount: number;
  digestionRate: string;
  storageLife: string;
  alerts: AlertWithMaster[];
  actions: SkuActionData[];
}

interface ColorRow {
  colorId: string;
  colorName: string;
  salesCounts: number[];
  salesPrices: number[];
  stockCount: number;
  digestionRate: string;
  storageLife: string;
  sizes: SizeRow[];
}

export type ActionStatus = "REQUEST" | "PENDING" | "COMPLETED";

export interface DataType {
  isLoading: boolean;
  itemNumber: string;
  today: string;
  warehouseNames: string[];
  skus: Sku[];
  alertDatas: AlertMaster[];
  showingAlert: boolean;
  selectedAlert: { alert: ProductAlert; type: string } | null;
  tableFormat: number;
  tableFormatChartList: number;
  monthRangeList: { name: string; id: number }[];
  selectedPeriod: number;
  xLabel: string[];
  yDataColumn: number[];
  yDataLine: number[];
  productDetailSalesDigestRateChartEntity: ProductDetailSalesDigestRateChartEntity;
  skuDetailSalesDigestRateChartData: ProductDetailSalesDigestRateChartData[];
  productAlerts: ProductAlert[];
  skuAlerts: ProductAlert[];
  productTableInfo: ProductDetailTrendEntity | null;
  skuTableTabs: SkuTrendTableTabs[];
  skuTableInfo: SkuDetailTrendEntity | null;
  produtTrendTableHeader: ProductTrendTableHeader[];
  skuSortBy: string[];
  skuSortDesc: boolean[];
  selectedSkuColor: SkuTrendTableTabs | null;
  selectedSkuSize: SkuSizeTrendTableData | null;
  productDetailImages: GetDetailImagesEntity;
  showingAction: boolean;
  actionInfo: ProductDetailActionInfoEntity;
  actionStatus: ActionStatus;
  customActionMasterList: CustomActionMaster[];
  actionList: Action[];
  customActionDetailList: CustomActionDetail[];
  adminUserDetails: AdminUserDetail[];
  stockActionDetailList: StockActionDetail[];
  selectedStockActionDetailIds: string[];
  showingCreateAction: boolean;
  createActionListInfo: { title: string | null; managerId: string | null; executedDate: Date | null };
  alertMessage: string;
  alertType: string;
  tableExpanded: string[];
  skuDisplayUnit: "fku" | "sku";
  isProductTableFullyShown: boolean;
  isSkuTableFullyShown: boolean;
  expandedTableHeight: string;
  tableHeadersBase: { text: string; value: string; sortable?: false; width: string }[];
  tableColors: ColorRow[];
  searchEntries: SearchEntriesInProductDetailTrend;
  skuFilterInfo: SkuFilterInfoEntity;
  productFilterStoreInput: FilterInfoDataEntity[]; // 集計対象外を「集計対象外」としてまとめた店舗一覧
}

export default Vue.extend({
  mixins: [mixin],
  components: {
    S3Image,
    ConfirmationDialog,
    highcharts: Chart,
    Loading,
    ElementDatePicker,
    FilterChipButton,
  },
  props: {
    propImages: {
      type: Array as () => string[],
      required: true,
    },
    user: {
      type: Object as () => User | null,
      required: false,
    },
    product: {
      type: Object as () => ProductDetailBasicInfoEntity,
      required: false,
    },
    stores: {
      type: Array as () => Store[],
      required: true,
    },
    warehouses: {
      type: Array as () => Warehouse[],
      required: true,
    },
    colors: {
      type: Array as () => Color[],
      required: true,
    },
    sizes: {
      type: Array as () => Size[],
      required: true,
    },
    propGraph: {
      type: Object as () => ProductDetailSalesDigestRateChartEntity,
      required: true,
    },
    propPeriod: {
      type: Number,
      required: true,
    },
  },
  data(): DataType {
    return {
      isLoading: false,
      itemNumber: "",
      // 本日日付
      today: dayjs().toString(),
      warehouseNames: [],
      skus: [],
      alertDatas: [],
      showingAlert: false,
      selectedAlert: null,
      tableFormat: 0,
      tableFormatChartList: 0,
      monthRangeList: [
        { name: "直近3ヶ月", id: 3 },
        { name: "直近6ヶ月", id: 6 },
        { name: "直近1年間", id: 12 },
        { name: "前年度", id: 24 },
        { name: "前々年度", id: 36 },
      ],
      selectedPeriod: 3,
      xLabel: [],
      yDataColumn: [],
      yDataLine: [],
      productDetailSalesDigestRateChartEntity: { productId: "", productData: null, skuData: [] },
      skuDetailSalesDigestRateChartData: [],
      productAlerts: [],
      skuAlerts: [],
      productTableInfo: null,
      skuTableTabs: [],
      skuTableInfo: null,
      produtTrendTableHeader: Constant.productTrendTableHeader,
      skuSortBy: ["unitPrice"],
      skuSortDesc: [true],
      selectedSkuColor: null,
      selectedSkuSize: null,
      productDetailImages: { s3KeyMain: "", s3KeySkus: [] },
      showingAction: false,
      actionInfo: { requests: [], pending: { saleActions: [], customActions: [], stockActions: [] }, completed: [] },
      actionStatus: "REQUEST",
      customActionMasterList: [],
      actionList: [],
      customActionDetailList: [],
      adminUserDetails: [],
      stockActionDetailList: [],
      selectedStockActionDetailIds: [],
      showingCreateAction: false,
      createActionListInfo: { title: null, managerId: null, executedDate: null },
      alertMessage: "",
      alertType: "success",
      tableExpanded: [],
      skuDisplayUnit: "fku",
      isProductTableFullyShown: false,
      isSkuTableFullyShown: false,
      expandedTableHeight: "824px",
      tableHeadersBase: [
        { text: "", value: "expand", sortable: false, width: "105px" }, // 固定
        { text: "カラー", value: "colorName", sortable: false, width: "150px" }, // 広め
        { text: "サイズ", value: "sizeName", sortable: false, width: "100px" },
        { text: "JANコード", value: "janCode", sortable: false, width: "150px" },
        { text: "年間売上", value: "salesYear", width: "100px" }, // 数値列は固定幅
        { text: "0週", value: "sales0", width: "80px" },
        { text: "1週", value: "sales1", width: "80px" },
        { text: "2週", value: "sales2", width: "80px" },
        { text: "3週", value: "sales3", width: "80px" },
        { text: "4週", value: "sales4", width: "80px" },
        { text: "在庫数", value: "stock", width: "80px" },
        { text: "消化率", value: "consumptionRate", width: "80px" },
        { text: "消化週数", value: "consumptionWeeks", width: "80px" },
        { text: "アラート", value: "alerts", sortable: false, width: "150px" },
        { text: "アクション", value: "actions", sortable: false, width: "150px" },
      ],
      tableColors: [],
      searchEntries: { alertMasters: [], stores: [] },
      productFilterStoreInput: [],
      skuFilterInfo: {
        brands: [],
        seasons: [],
        categories: [],
        attribute1: [],
        attribute2: [],
        attribute3: [],
        stores: [],
        colors: [],
        sizes: [],
        alertMasters: [],
      },
    };
  },
  computed: {
    productTableHeight(): string {
      if (!this.productTableInfo || this.productTableInfo.items.length <= 9) return "auto";
      // 拡大時はSKUテーブルを非表示にし、画面下端までの実測値(expandedTableHeight)まで広げる
      return this.isProductTableFullyShown ? this.expandedTableHeight : "384px";
    },
    skuTableHeight(): string {
      if (!this.productTableInfo || this.productTableInfo.items.length <= 9) return "auto";
      // 拡大時は品番テーブルを非表示にし、画面下端までの実測値(expandedTableHeight)まで広げる
      return this.isSkuTableFullyShown ? this.expandedTableHeight : "384px";
    },
    tableHeaders() {
      const sortableKeys = [
        "salesYear",
        "sales0",
        "sales1",
        "sales2",
        "sales3",
        "sales4",
        "stock",
        "consumptionRate",
        "consumptionWeeks",
      ];

      return this.tableHeadersBase.map((h) => {
        const sortable = sortableKeys.includes(h.value);

        // ★ expand 列だけ width を固定
        if (h.value === "expand") {
          return {
            ...h,
            sortable,
            width: 105, // ← 幅固定
            align: "center" as const, // （任意）中央寄せしたい場合
          };
        }

        return {
          ...h,
          sortable,
        };
      });
    },
    tableDisplayItems(): any[] {
      const rows: any[] = [];
      this.tableColors.forEach((color) => {
        // FKU単位表示ではFKU(カラー)行を常に表示する
        if (this.skuDisplayUnit === "fku") {
          rows.push({
            key: color.colorId,
            isColorRow: true,
            colorId: color.colorId,
            colorName: color.colorName,
            salesYear: this.tableFormat === 0 ? color.salesCounts[0] : color.salesPrices[0],
            sales0: this.tableFormat === 0 ? color.salesCounts[1] : color.salesPrices[1],
            sales1: this.tableFormat === 0 ? color.salesCounts[2] : color.salesPrices[2],
            sales2: this.tableFormat === 0 ? color.salesCounts[3] : color.salesPrices[3],
            sales3: this.tableFormat === 0 ? color.salesCounts[4] : color.salesPrices[4],
            sales4: this.tableFormat === 0 ? color.salesCounts[5] : color.salesPrices[5],
            stock: color.stockCount,
            consumptionRate: color.digestionRate,
            consumptionWeeks: color.storageLife,
          });
        }

        const shouldShowSizes = this.skuDisplayUnit === "sku" || this.tableExpanded.includes(color.colorId);

        if (shouldShowSizes) {
          color.sizes.forEach((size) => {
            rows.push({
              key: `${color.colorId}-${size.sizeId}`,
              isColorRow: false,
              colorId: size.colorId,
              colorName: color.colorName || color.colorId,
              sizeName: size.sizeName,
              janCode: size.janCode,
              salesYear: this.tableFormat === 0 ? size.salesCounts[0] : size.salesPrices[0],
              sales0: this.tableFormat === 0 ? size.salesCounts[1] : size.salesPrices[1],
              sales1: this.tableFormat === 0 ? size.salesCounts[2] : size.salesPrices[2],
              sales2: this.tableFormat === 0 ? size.salesCounts[3] : size.salesPrices[3],
              sales3: this.tableFormat === 0 ? size.salesCounts[4] : size.salesPrices[4],
              sales4: this.tableFormat === 0 ? size.salesCounts[5] : size.salesPrices[5],
              stock: size.stockCount,
              consumptionRate: size.digestionRate,
              consumptionWeeks: size.storageLife,
              alerts: size.alerts,
              actions: size.actions,
            });
          });
        }
      });

      return rows;
    },
    // 値引率
    percentOff(): string {
      if (this.product?.salesPrice && this.product.price) {
        if (this.product.salesPrice < this.product.price) {
          const off = 100 - (this.product.salesPrice / this.product.price) * 100;
          return `(${Math.floor(off)}% OFF)`;
        }
      }
      return "";
    },
    // 週間、画面表示フォーマット
    oneWeek() {
      return (minusWeek = 0): { from: string; to: string } => {
        const week = this.oneWeekDayjs(minusWeek);
        return { from: week.from.format("MM.DD"), to: week.to.format("MM.DD") };
      };
    },
    // 日付の差分
    diffDate(): string {
      if (!this.product?.salesStartedAt || !this.product?.salesEndedAt) {
        return "未定";
      }
      return (
        (
          dayjs(this.product?.salesEndedAt.toString()).diff(dayjs(this.product?.salesStartedAt.toString()), "days") + 1
        ).toString() + "日"
      );
    },
    options(): Options {
      const labels: any =
        this.tableFormat === 0
          ? {
              formatter: function () {
                return Highcharts.numberFormat(Number(this.value), 0, ",", ",");
              },
              style: {
                // Y軸の各ポイントにおけるカラー（assistantType50で統一）
                color: "#799498",
              },
            }
          : {
              formatter: function () {
                return `￥${Highcharts.numberFormat(Number(this.value), 0, ",", ",")}`;
              },
              style: {
                // Y軸の各ポイントにおけるカラー（assistantType50で統一）
                color: "#799498",
              },
            };
      const yDataColumnMax = Math.max(...this.yDataColumn);
      const tickInterval = this.selectedPeriod === 3 ? 2 : this.selectedPeriod === 6 ? 3 : 2;
      const tooltip: TooltipOptions =
        this.tableFormat === 0
          ? {
              formatter: function () {
                let s = this.x.toString();
                if (this.points) {
                  s +=
                    "<br/>" +
                    '<b style="color:#76B3E0">' +
                    this.points[0].series.name +
                    "</b>" +
                    ": " +
                    this.points[0].y.toLocaleString();
                  s +=
                    "<br/>" +
                    '<b style="color:#519998">' +
                    this.points[1].series.name +
                    "</b>" +
                    ": " +
                    this.points[1].y +
                    "%";
                }
                return s;
              },
              shared: true,
              style: {
                // ツールチップ内の文字カラーはassistantType70
                color: "#3C6065",
                fontSize: "12px",
              },
            }
          : {
              formatter: function () {
                let s = this.x.toString();
                if (this.points) {
                  s +=
                    "<br/>" +
                    '<b style="color:#76B3E0">' +
                    this.points[0].series.name +
                    "</b>" +
                    ": " +
                    "￥" +
                    this.points[0].y.toLocaleString();
                  s +=
                    "<br/>" +
                    '<b style="color:#519998">' +
                    this.points[1].series.name +
                    "</b>" +
                    ": " +
                    this.points[1].y +
                    "%";
                }
                return s;
              },
              shared: true,
              style: {
                // ツールチップ内の文字カラーはassistantType70
                color: "#3C6065",
                fontSize: "12px",
              },
            };
      return {
        title: undefined,
        chart: {
          width: this.showingAction ? 786 : 998,
          height: 215,
          backgroundColor: "#fbfbfb",
        },
        credits: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
        xAxis: [
          {
            categories: this.xLabel,
            tickInterval: tickInterval, // one month
            gridLineWidth: 1,
            labels: {
              style: {
                // X軸の各ポイントにおけるカラー（assistantType50で統一）
                color: "#799498",
              },
            },
          },
        ],
        yAxis: [
          {
            // 1つ目のy軸設定
            title: {
              text: undefined,
            },
            endOnTick: false,
            min: 0,
            max:
              (Math.floor(yDataColumnMax / Math.pow(10, yDataColumnMax.toString().length - 1)) + 1) *
              Math.pow(10, yDataColumnMax.toString().length - 1),
            tickInterval:
              ((Math.floor(yDataColumnMax / Math.pow(10, yDataColumnMax.toString().length - 1)) + 1) *
                Math.pow(10, yDataColumnMax.toString().length - 1)) /
              5, // y軸のgrid幅を揃える
            // labels: {
            //   format: this.tableFormat === 0 ? `` : `￥{value}`
            // }
            labels: labels,
          },
          {
            // 2つ目のy軸設定
            title: {
              text: undefined,
            },
            labels: {
              format: "{value}%",
              style: {
                // Y軸の各ポイントにおけるカラー（assistantType50で統一）
                color: "#799498",
              },
            },
            endOnTick: false,
            min: 0,
            max: 100,
            tickInterval: 20, // y軸のgrid幅を揃える
            opposite: true, // trueの場合グラフの右側にy軸を配置する
          },
        ],
        tooltip: tooltip,
        plotOptions: {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: false,
              color: "#13365c",
              verticalAlign: "bottom",
              useHTML: true,
            },
          },
          line: {
            dataLabels: {
              enabled: false,
              color: "#006400",
              verticalAlign: "bottom",
              useHTML: true,
            },
          },
        },
        series: [
          {
            // 1つ目のグラフデータ
            name: "売上", // グラフの名前指定
            type: "column", // グラフの形指定
            data: this.yDataColumn, // グラフに使用するデータ指定
            yAxis: 0, // y軸を指定
            color: "#76B3E0",
            pointWidth: 15,
          },
          {
            // 2つ目のグラフデータ
            name: "消化率", // グラフの名前指定
            type: "line", // グラフの形指定
            data: this.yDataLine, // グラフに使用するデータ指定
            yAxis: 1, // y軸を指定
            color: "#519998",
            marker: {
              fillColor: "#FFFFFF",
              radius: 5,
              lineWidth: 2,
              lineColor: undefined, // inherit from series
            },
          },
        ],
      };
    },
    selectedActionType(): ActionType | null {
      if (this.selectedStockActionDetailIds.length === 0) {
        // 選択されているリクエストがない場合
        return null;
      } else {
        const selectedStockActionDetails = this.stockActionDetailList.filter((stockAction) =>
          this.selectedStockActionDetailIds.includes(stockAction.id)
        );
        const actionTypeList = selectedStockActionDetails.map((stockActionDetail) =>
          stockActionDetail.sourceStoreId ? "STOCKMOVING" : "STOCKFOLLOW"
        );
        if (actionTypeList.every((actionType) => actionType === actionTypeList[0])) {
          // 選択されているリクエストで異なるアクションタイプがない
          return actionTypeList[0];
        } else {
          // 選択されているリクエストで異なるアクションタイプがある
          return null;
        }
      }
    },
    requestActionNumber(): number {
      return this.actionInfo.requests.reduce((sum, request) => {
        return (
          sum +
          request.stockActionDetails.reduce((stockNumber, stockActionDetail) => {
            return stockNumber + stockActionDetail.stockFollowInfos.length + stockActionDetail.stockMovingInfos.length;
          }, 0)
        );
      }, 0);
    },
    pendingActionNumber(): number {
      return (
        this.actionInfo.pending.saleActions.length +
        this.actionInfo.pending.customActions.length +
        this.actionInfo.pending.stockActions.length
      );
    },
    completedActionNumber(): number {
      return this.actionInfo.completed.reduce((sum, orderedActionInfo) => {
        return (
          sum +
          orderedActionInfo.saleActions.length +
          orderedActionInfo.customActions.length +
          orderedActionInfo.stockActions.length
        );
      }, 0);
    },
    isAllCollapsed() {
      return this.tableExpanded.length === 0;
    },
  },
  methods: {
    toggleProductTableExpand() {
      this.isProductTableFullyShown = !this.isProductTableFullyShown;
      if (this.isProductTableFullyShown) this.fitExpandedTableToViewport("productTableCard");
    },
    toggleSkuTableExpand() {
      this.isSkuTableFullyShown = !this.isSkuTableFullyShown;
      if (this.isSkuTableFullyShown) this.fitExpandedTableToViewport("skuTableCard");
    },
    fitExpandedTableToViewport(refName: string) {
      // もう一方のテーブルが非表示になった後の位置で、カード上端から画面下端までの高さを算出する
      this.$nextTick(() => {
        const card = this.$refs[refName] as Vue | undefined;
        if (!card) return;
        const top = (card.$el as HTMLElement).getBoundingClientRect().top;
        this.expandedTableHeight = `${Math.max(window.innerHeight - top, 384)}px`;
      });
    },
    async search() {
      if (!this.product) return;
      this.isLoading = true;
      const alerts = this.searchEntries.alertMasters.map((a) => Number(a.id));
      const stores = this.searchEntries.stores.map((store) =>
        typeof store.id === "string" ? store.id : "inoperative"
      );
      // FKU(カラー)集計を再取得したうえで、必要なSKU(サイズ)情報をカラー単位APIで取得する
      const res = await RadialApiClient.getSkuColorSizeTableData(this.product.id, undefined, {
        alerts,
        stores,
      });
      if (res) {
        this.applyTableData(res);
      }
      if (alerts.length > 0) {
        // アラート検索時: 全カラーのSKUを取得し、該当SKUを1つも含まないFKUは表示しない
        await this.ensureSizesLoaded(this.tableColors.map((c) => c.colorId));
        this.tableColors = this.tableColors.filter((c) => c.sizes.length > 0);
        this.tableExpanded = this.tableExpanded.filter((id) => this.tableColors.some((c) => c.colorId === id));
      } else {
        // SKU単位表示は全カラー、FKU単位表示は展開中カラーのSKUを取得する（展開状態を維持）
        await this.ensureSizesLoaded(
          this.skuDisplayUnit === "sku" ? this.tableColors.map((c) => c.colorId) : [...this.tableExpanded]
        );
      }
      this.isLoading = false;
    },
    // APIレスポンスからテーブル表示用のデータを構築する（展開状態は存在するカラーのみ維持）
    applyTableData(res: SkuColorSizeDetailTrendEntity) {
      this.tableColors = res.colorTotals.map((c) => ({
        colorId: c.colorId ?? "",
        colorName: this.getColorName(c.colorId ?? ""), // 任意: ID→名前変換用
        salesCounts: c.salesCounts,
        salesPrices: c.salesPrices,
        stockCount: c.stockCount,
        digestionRate: c.digestionRate,
        storageLife: c.storageLife,
        sizes: res.sizes.filter((s) => s.colorId === c.colorId).map((s) => this.mapSizeRow(s)),
      }));
      this.tableExpanded = this.tableExpanded.filter((id) => this.tableColors.some((c) => c.colorId === id));
    },
    // サイズ未取得のカラーをカラー単位APIで個別に取得する（アラート検索中は該当SKUのみが返る）
    async ensureSizesLoaded(colorIds: string[]) {
      if (!this.product) return;
      const productId = this.product.id;
      const alerts = this.searchEntries.alertMasters.map((a) => Number(a.id));
      const stores = this.searchEntries.stores.map((store) =>
        typeof store.id === "string" ? store.id : "inoperative"
      );
      const targets = this.tableColors.filter((c) => colorIds.includes(c.colorId) && c.sizes.length === 0);
      await Promise.all(
        targets.map(async (color) => {
          const res = await RadialApiClient.getSkuColorSizeTableData(productId, color.colorId, { alerts, stores });
          if (res) {
            color.sizes = res.sizes.map((s) => this.mapSizeRow(s));
          }
        })
      );
    },
    mapSizeRow(s: ColorSizeDetailTrendItemEntity): SizeRow {
      return {
        colorId: s.colorId ?? "",
        sizeId: s.sizeId ?? "",
        sizeName: this.getSizeName(s.sizeId ?? ""),
        janCode: s.janCode,
        salesCounts: s.salesCounts,
        salesPrices: s.salesPrices,
        stockCount: s.stockCount,
        digestionRate: s.digestionRate,
        storageLife: s.storageLife,
        alerts: s.alertDatas,
        actions: s.actions ?? [],
      };
    },
    // FKU単位表示に切り替える（FKU単位表示中は全展開を閉じるボタンとして機能する）
    showFkuUnit() {
      this.skuDisplayUnit = "fku";
      this.tableExpanded = [];
    },
    // SKU単位表示に切り替える（FKU行を消して全SKUのみを表示する）
    async showSkuUnit() {
      if (!this.product) return;
      this.isLoading = true;
      const alerts = this.searchEntries.alertMasters.map((a) => Number(a.id));
      const stores = this.searchEntries.stores.map((store) =>
        typeof store.id === "string" ? store.id : "inoperative"
      );
      const res = await RadialApiClient.getSkuColorSizeTableData(this.product.id, undefined, {
        alerts,
        stores,
      });
      if (res) {
        this.applyTableData(res);
      }
      // 全カラーのSKUをカラー単位APIで取得する
      await this.ensureSizesLoaded(this.tableColors.map((c) => c.colorId));
      this.skuDisplayUnit = "sku";
      this.tableExpanded = [];
      this.isLoading = false;
    },
    async toggleExpand(colorId: string) {
      if (!this.product) return;
      this.isLoading = true;
      const idx = this.tableExpanded.indexOf(colorId);
      if (idx !== -1) {
        this.tableExpanded.splice(idx, 1);
        this.isLoading = false;
        return;
      }

      // APIでサイズ情報を取得（アラート検索中は該当SKUのみが返る）
      const alerts = this.searchEntries.alertMasters.map((a) => Number(a.id));
      const stores = this.searchEntries.stores.map((store) =>
        typeof store.id === "string" ? store.id : "inoperative"
      );
      const res = await RadialApiClient.getSkuColorSizeTableData(this.product.id, colorId, {
        alerts,
        stores,
      });
      const color = this.tableColors.find((c) => c.colorId === colorId);
      if (color && res) {
        color.sizes = res.sizes.map((s) => this.mapSizeRow(s));
      }

      this.tableExpanded.push(colorId);
      this.isLoading = false;
    },
    // SKUテーブルのソート
    // FKU単位: FKU(カラー)行同士を比較してソートし、子SKUはサイズ順のまま親に追随させる
    // SKU単位: 全SKUを通常通りソートする
    skuTableSort(items: any[], sortBy: string[], sortDesc: boolean[]) {
      const key = sortBy[0];
      if (!key) return items;
      const ascValue = sortDesc[0] ? -1 : 1;
      const getValue = (row: any): number => {
        switch (key) {
          case "consumptionRate":
            return row.consumptionRate === "-" ? -Infinity : parseFloat(row.consumptionRate);
          case "consumptionWeeks":
            return row.consumptionWeeks === "∞週" ? Infinity : parseFloat(row.consumptionWeeks);
          default:
            return Number(row[key]) || 0;
        }
      };
      const compare = (a: any, b: any) => (getValue(a) - getValue(b)) * ascValue;

      if (this.skuDisplayUnit === "sku") {
        return [...items].sort(compare);
      }

      // カラーごとにFKU行と子SKU行をまとめてグループ化する
      const groupMap = new Map<string, { parent?: any; children: any[] }>();
      const colorOrder: string[] = [];
      items.forEach((row) => {
        if (!groupMap.has(row.colorId)) {
          groupMap.set(row.colorId, { children: [] });
          colorOrder.push(row.colorId);
        }
        const group = groupMap.get(row.colorId)!;
        if (row.isColorRow) {
          group.parent = row;
        } else {
          group.children.push(row);
        }
      });
      const groups = colorOrder.map((id) => groupMap.get(id)!);
      groups.sort((a, b) => (a.parent && b.parent ? compare(a.parent, b.parent) : 0));

      const rows: any[] = [];
      groups.forEach((group) => {
        if (group.parent) rows.push(group.parent);
        rows.push(...group.children);
      });
      return rows;
    },
    customSort(items: DetailTrendItemEntity[], index: string[], isDesc: boolean[]) {
      const ascValue = isDesc[0] ? -1 : 1;
      items.sort((a, b) => {
        switch (index[0]) {
          case "storageLife":
            if (a.storageLife === "∞週") return ascValue;
            if (b.storageLife === "∞週") return -ascValue;
            return (parseFloat(a.storageLife) - parseFloat(b.storageLife)) * ascValue;
          case "digestionRate":
            if (a.digestionRate === "-") return -ascValue;
            if (b.digestionRate === "-") return ascValue;
            return (parseFloat(a.digestionRate) - parseFloat(b.digestionRate)) * ascValue;
          case "stockCount":
            return (a.stockCount - b.stockCount) * ascValue;
          case "unitPrice":
            if (this.tableFormat === 0) {
              return (a.salesCounts[0] - b.salesCounts[0]) * ascValue;
            } else {
              return (a.salesPrices[0] - b.salesPrices[0]) * ascValue;
            }
          case "0week":
            if (this.tableFormat === 0) {
              return (a.salesCounts[1] - b.salesCounts[1]) * ascValue;
            } else {
              return (a.salesPrices[1] - b.salesPrices[1]) * ascValue;
            }
          case "-1week":
            if (this.tableFormat === 0) {
              return (a.salesCounts[2] - b.salesCounts[2]) * ascValue;
            } else {
              return (a.salesPrices[2] - b.salesPrices[2]) * ascValue;
            }
          case "-2week":
            if (this.tableFormat === 0) {
              return (a.salesCounts[3] - b.salesCounts[3]) * ascValue;
            } else {
              return (a.salesPrices[3] - b.salesPrices[3]) * ascValue;
            }
          case "-3week":
            if (this.tableFormat === 0) {
              return (a.salesCounts[4] - b.salesCounts[4]) * ascValue;
            } else {
              return (a.salesPrices[4] - b.salesPrices[4]) * ascValue;
            }
          case "-4week":
            if (this.tableFormat === 0) {
              return (a.salesCounts[5] - b.salesCounts[5]) * ascValue;
            } else {
              return (a.salesPrices[5] - b.salesPrices[5]) * ascValue;
            }
          default:
            return 0;
        }
      });
      return items;
    },
    getColorDetail(id: string): ColorDetail {
      const colorId = this.skus.find((sku) => sku.id === id)?.colorId;
      const color = this.colors.find((color) => color.id === colorId);
      return {
        id: colorId ?? "",
        name: color?.name ?? "",
      };
    },
    getSkuId(skuColor: SkuTrendTableTabs): string {
      return skuColor.sizes.find((size) => size.sizeId === this.selectedSkuSize?.sizeId)?.skuId ?? "";
    },
    getColorName(colorId: string): string {
      return this.colors.find((color) => color.id === colorId)?.name ?? "";
    },
    getSizeDetail(id: string): SizeDetail {
      const sizeId = this.skus.find((sku) => sku.id === id)?.sizeId;
      const size = this.sizes.find((size) => size.id === sizeId);
      return {
        id: sizeId ?? "",
        name: size?.name ?? "",
      };
    },
    getSizeName(sizeId: string): string {
      return this.sizes.find((size) => size.id === sizeId)?.name ?? "";
    },
    formatDate(date: Date): string {
      if (date !== null) {
        return date.toString().substring(0, 10).replaceAll("-", "/");
      } else {
        return "";
      }
    },
    // １週間日付取得
    oneWeekDayjs(minusWeek = 0): { from: Dayjs; to: Dayjs } {
      let to: Dayjs = dayjs();
      let from: Dayjs = dayjs();
      // 曜日取得
      const day = dayjs(this.today).day();
      // 本日、日曜日の場合先週の月曜日から今日(日曜日)までを範囲とする
      // 月曜日始まりにするため下でaddしている(dayjsの週間始まりは日曜日)のとバランスをとるため
      if (day === 0) {
        minusWeek += 1;
      }
      from = dayjs(this.today).subtract(minusWeek, "week").startOf("weeks").add(1, "day");
      if (minusWeek > 0) {
        to = dayjs(this.today).subtract(minusWeek, "week").endOf("weeks").add(1, "day");
      } else {
        to = dayjs(this.today).endOf("day");
      }
      return { from: from, to: to };
    },
    getAlertDetail(alertMasterId: number): AlertDetail {
      const alert = this.alertDatas.find((item) => {
        return item.id === alertMasterId;
      });
      return {
        number: alert?.id ?? -1,
        name: alert?.name ?? "",
        color: alert?.prominenceColor ?? "",
        storeIntegration: alert?.storeIntegration ?? false,
        description: alert?.description ?? "",
        comment: alert?.comment ?? "",
      };
    },
    selectDeleteAlert(alert: ProductAlert, type: string) {
      this.selectedAlert = { alert, type };
      this.showAlert(
        `アラートを無効化しますか？
           しばらくの間フィルター検索後の結果に表示されなくなり、対応すべきアラートを集中して管理することが可能になります。`,
        "warning"
      );
      this.showingAlert = true;
    },
    async deleteAlert() {
      if (this.selectedAlert) {
        await RadialApiClient.userDeleteAlert(this.selectedAlert.alert.alertId);
        if (this.selectedAlert.type === "product") {
          this.productAlerts.filter((alert) => alert.alertId === this.selectedAlert?.alert.alertId)[0].userDeleted =
            true;
        } else if (this.selectedAlert.type === "sku") {
          this.skuAlerts.filter((alert) => alert.alertId === this.selectedAlert?.alert.alertId)[0].userDeleted = true;
        }
      }
      this.selectedAlert = null;
      this.showingAlert = false;
    },
    getMainImage(skuId: string): string {
      if (this.product) {
        return this.productDetailImages.s3KeySkus.find((item) => item.skuId === skuId)?.s3KeyWithId[0]?.s3Key ?? "";
      }
      return "";
    },
    getImageFromProduct(product: ProductDetailBasicInfoEntity): string {
      const images = this.propImages.filter((image) =>
        image.startsWith(`${this.user?.companyId}/${product.itemNumber}_`)
      );
      if (images.length > 0) {
        const mainImages = images.filter((image) => image.endsWith("_M"));
        if (mainImages.length > 0) {
          return mainImages[0];
        } else {
          return images[0];
        }
      } else {
        return "";
      }
    },
    getTotalSales(type: string): string {
      if (this.productDetailSalesDigestRateChartEntity.productData) {
        switch (type) {
          case "total":
            return this.tableFormat === 0
              ? this.productDetailSalesDigestRateChartEntity.productData.totalSalesCount?.toLocaleString() ?? "0"
              : `¥${this.productDetailSalesDigestRateChartEntity.productData.totalSalesPrice?.toLocaleString() ?? "0"}`;
          case "store":
            return this.tableFormat === 0
              ? this.productDetailSalesDigestRateChartEntity.productData.posSalesCount?.toLocaleString() ?? "0"
              : `¥${this.productDetailSalesDigestRateChartEntity.productData.posSalesPrice?.toLocaleString() ?? "0"}`;
          case "ec":
            return this.tableFormat === 0
              ? this.productDetailSalesDigestRateChartEntity.productData.ecSalesCount?.toLocaleString() ?? "0"
              : `¥${this.productDetailSalesDigestRateChartEntity.productData.ecSalesPrice?.toLocaleString() ?? "0"}`;
          default:
            return "";
        }
      } else {
        return "";
      }
    },
    getTotalStocks(type: string): string {
      if (this.productDetailSalesDigestRateChartEntity.productData) {
        switch (type) {
          case "digestionRate":
            return this.productDetailSalesDigestRateChartEntity.productData.digestionRate.toLocaleString();
          case "storageLife":
            return this.productDetailSalesDigestRateChartEntity.productData.storageLife.replace("週", "");
          case "stockCount":
            // TODO: サーバー側で在庫金額も送ってもらう
            return this.productDetailSalesDigestRateChartEntity.productData.stockCount.toLocaleString();
          case "discountRate":
            return this.productDetailSalesDigestRateChartEntity.productData.discountRate.toLocaleString();
          case "grossProfitMargin":
            return this.productDetailSalesDigestRateChartEntity.productData.grossProfitMargin.toLocaleString();
          default:
            return "";
        }
      } else {
        return "";
      }
    },
    getSkuSales(type: string, sku: ProductDetailSalesDigestRateChartData): string {
      switch (type) {
        case "total":
          return this.tableFormat === 0
            ? sku.totalSalesCount?.toLocaleString() ?? "0"
            : `¥${sku.totalSalesPrice?.toLocaleString() ?? "0"}`;
        case "store":
          return this.tableFormat === 0
            ? sku.posSalesCount?.toLocaleString() ?? "0"
            : `¥${sku.posSalesPrice?.toLocaleString() ?? "0"}`;
        case "ec":
          return this.tableFormat === 0
            ? sku.ecSalesCount?.toLocaleString() ?? "0"
            : `¥${sku.ecSalesPrice?.toLocaleString() ?? "0"}`;
        default:
          return "";
      }
    },
    getSkuStockCount(sku: ProductDetailSalesDigestRateChartData): string {
      // TODO: サーバー側でStockPriceも送ってもらう
      return this.tableFormat === 0 ? sku.stockCount.toLocaleString() : sku.stockCount.toLocaleString();
    },
    getSkuSalesMax(): number {
      let sales: number[] = [];
      for (const sale of this.productDetailSalesDigestRateChartEntity.skuData
        .map((element) => element.data)
        .map((element) =>
          element.map((element) => (this.tableFormat === 0 ? element.salesQuantity : element.salesPrice))
        )) {
        sales = sales.concat(sale);
      }
      return Math.max(...sales);
    },
    getSkuOptions(skuid: string): Options {
      const yDataSkuColumn = this.productDetailSalesDigestRateChartEntity.skuData
        .find((element) => element.id === skuid)
        ?.data.map((element) => (this.tableFormat === 0 ? element.salesQuantity : element.salesPrice ?? 0));
      const yDataSkuLine = this.productDetailSalesDigestRateChartEntity.skuData
        .find((element) => element.id === skuid)
        ?.data.map((element) => Math.ceil(Number(element.digestRate)) ?? 0);
      const labels: any =
        this.tableFormat === 0
          ? {
              formatter: function () {
                return Highcharts.numberFormat(Number(this.value), 0, ",", ",");
              },
              style: {
                // Y軸の各ポイントにおけるカラー（assistantType50で統一）
                color: "#799498",
              },
            }
          : {
              formatter: function () {
                return `￥${Highcharts.numberFormat(Number(this.value), 0, ",", ",")}`;
              },
              style: {
                // Y軸の各ポイントにおけるカラー（assistantType50で統一）
                color: "#799498",
              },
            };
      const yDataSkuColumnMax = this.getSkuSalesMax();
      const tickInterval = this.selectedPeriod === 3 ? 2 : this.selectedPeriod === 6 ? 3 : 2;
      const tooltip: TooltipOptions =
        this.tableFormat === 0
          ? {
              formatter: function () {
                let s = this.x.toString();
                if (this.points) {
                  s +=
                    "<br/>" +
                    '<b style="color:#76B3E0">' +
                    this.points[0].series.name +
                    "</b>" +
                    ": " +
                    this.points[0].y.toLocaleString();
                  s +=
                    "<br/>" +
                    '<b style="color:#519998">' +
                    this.points[1].series.name +
                    "</b>" +
                    ": " +
                    this.points[1].y +
                    "%";
                }
                return s;
              },
              shared: true,
              style: {
                // ツールチップ内の文字カラーはassistantType70
                color: "#3C6065",
                fontSize: "12px",
              },
            }
          : {
              formatter: function () {
                let s = this.x.toString();
                if (this.points) {
                  s +=
                    "<br/>" +
                    '<b style="color:#76B3E0">' +
                    this.points[0].series.name +
                    "</b>" +
                    ": " +
                    "￥" +
                    this.points[0].y.toLocaleString();
                  s +=
                    "<br/>" +
                    '<b style="color:#519998">' +
                    this.points[1].series.name +
                    "</b>" +
                    ": " +
                    this.points[1].y +
                    "%";
                }
                return s;
              },
              shared: true,
              style: {
                // ツールチップ内の文字カラーはassistantType70
                color: "#3C6065",
                fontSize: "12px",
              },
            };
      if (yDataSkuColumn && yDataSkuLine) {
        return {
          title: undefined,
          chart: {
            width: this.showingAction ? 390 : 480,
            height: 160,
            backgroundColor: "#fbfbfb",
          },
          credits: {
            enabled: false,
          },
          legend: {
            enabled: false,
          },
          xAxis: [
            {
              categories: this.xLabel,
              tickInterval: tickInterval, // one month
              gridLineWidth: 1,
              labels: {
                style: {
                  // X軸の各ポイントにおけるカラー（assistantType50で統一）
                  color: "#799498",
                },
              },
            },
          ],
          yAxis: [
            {
              labels: labels,
              // 1つ目のy軸設定
              title: {
                text: undefined,
              },
              endOnTick: false,
              min: 0,
              max:
                (Math.floor(yDataSkuColumnMax / Math.pow(10, yDataSkuColumnMax.toString().length - 1)) + 1) *
                Math.pow(10, yDataSkuColumnMax.toString().length - 1),
              tickInterval:
                ((Math.floor(yDataSkuColumnMax / Math.pow(10, yDataSkuColumnMax.toString().length - 1)) + 1) *
                  Math.pow(10, yDataSkuColumnMax.toString().length - 1)) /
                5, // y軸のgrid幅を揃える
            },
            {
              // 2つ目のy軸設定
              title: {
                text: undefined,
              },
              labels: {
                format: "{value}%",
                enabled: true, // y軸のメモリを表示
                style: {
                  // Y軸の各ポイントにおけるカラー（assistantType50で統一）
                  color: "#799498",
                },
              },
              endOnTick: false,
              min: 0,
              max: 100,
              tickInterval: 20, // y軸のgrid幅を揃える
              opposite: true, // trueの場合グラフの右側にy軸を配置する
            },
          ],
          plotOptions: {
            column: {
              stacking: "normal",
              dataLabels: {
                enabled: false,
                color: "#13365c",
                verticalAlign: "bottom",
                useHTML: true,
              },
            },
            line: {
              dataLabels: {
                enabled: false,
                color: "#006400",
                verticalAlign: "bottom",
                useHTML: true,
              },
            },
          },
          tooltip: tooltip,
          series: [
            {
              // 1つ目のグラフデータ
              name: "売上", // グラフの名前指定
              type: "column", // グラフの形指定
              data: yDataSkuColumn, // グラフに使用するデータ指定
              yAxis: 0, // y軸を指定
              color: "#76B3E0",
              pointWidth: 7,
            },
            {
              // 2つ目のグラフデータ
              name: "消化率", // グラフの名前指定
              type: "line", // グラフの形指定
              data: yDataSkuLine, // グラフに使用するデータ指定
              yAxis: 1, // y軸を指定
              color: "#519998",
              marker: {
                fillColor: "#FFFFFF",
                radius: 2,
                lineWidth: 1,
                lineColor: undefined, // inherit from series
              },
            },
          ],
        };
      } else {
        return {
          title: undefined,
          chart: {
            width: 305,
            height: 200,
            backgroundColor: "#fbfbfb",
          },
          credits: {
            enabled: false,
          },
          legend: {
            enabled: false,
          },
        };
      }
    },
    getSkuTrendAlertDatas(skuId: string): ProductAlert[] {
      return this.skuAlerts.filter((alert) => alert.id === skuId);
    },
    async searchAdditionalSkus() {
      if (!this.product) {
        return;
      }
      this.isLoading = true;
      // もっと見るで追加でSKUのグラフ情報をサーチ
      const productDetailSalesDigestRateChartEntity = await RadialApiClient.getProductDetailTrendChartsValue(
        this.product.id,
        this.selectedPeriod
      );
      if (productDetailSalesDigestRateChartEntity) {
        this.productDetailSalesDigestRateChartEntity.skuData =
          this.productDetailSalesDigestRateChartEntity.skuData.concat(productDetailSalesDigestRateChartEntity.skuData);
        this.skuDetailSalesDigestRateChartData = { ...this.productDetailSalesDigestRateChartEntity.skuData };
        // 親コンポーネントのデータ更新
        this.$emit("update-graph", this.productDetailSalesDigestRateChartEntity);
      } else {
        this.showAlert("エラーが発生しております。\n時間を空けてからもう一度お試しください。", "error");
      }
      this.isLoading = false;
    },
    async selectPeriod() {
      this.isLoading = true;
      const productDetailSalesDigestRateChartEntity = await RadialApiClient.getProductDetailTrendChartsValue(
        this.product.id,
        this.selectedPeriod
      );
      if (productDetailSalesDigestRateChartEntity) {
        this.productDetailSalesDigestRateChartEntity = productDetailSalesDigestRateChartEntity;
        // 親コンポーネントのデータ更新
        this.$emit("update-period", this.selectedPeriod);
        this.$emit("update-graph", productDetailSalesDigestRateChartEntity);
      }
      this.skuDetailSalesDigestRateChartData = { ...this.productDetailSalesDigestRateChartEntity.skuData };
      this.xLabel =
        this.productDetailSalesDigestRateChartEntity.productData?.data.map(
          (element) => `${dayjs(element.date).format("MM.DD")}`
        ) ?? [];
      if (this.tableFormat === 0) {
        this.yDataColumn =
          this.productDetailSalesDigestRateChartEntity.productData?.data.map((element) => element.salesQuantity ?? 0) ??
          [];
      } else {
        this.yDataColumn =
          this.productDetailSalesDigestRateChartEntity.productData?.data.map((element) => element.salesPrice ?? 0) ??
          [];
      }
      this.yDataLine =
        this.productDetailSalesDigestRateChartEntity.productData?.data.map(
          (element) => Math.ceil(Number(element.digestRate)) ?? 0
        ) ?? [];
      this.isLoading = false;
    },
    selectTableFormat() {
      if (this.tableFormat === 0) {
        // select quantity
        this.yDataColumn =
          this.productDetailSalesDigestRateChartEntity.productData?.data.map((element) => element.salesQuantity ?? 0) ??
          [];
      } else {
        // this.tableFormat === 1 // select price
        this.yDataColumn =
          this.productDetailSalesDigestRateChartEntity.productData?.data.map((element) => element.salesPrice ?? 0) ??
          [];
      }
    },
    async selectTrendFormat() {
      this.isLoading = true;
      if (this.tableFormatChartList === 1) {
        this.productTableInfo = await RadialApiClient.getProductDetailTrends(this.$route.params.productId);
        this.productTableInfo?.items.sort((a, b) => {
          const aName = this.getChanelName(a.storeId, a.warehouseId);
          const bName = this.getChanelName(b.storeId, b.warehouseId);

          // 店舗側か倉庫側かを判定
          const isAStore = a.storeId !== null;
          const isBStore = b.storeId !== null;

          if (isAStore && !isBStore) {
            return -1; // 店舗を優先
          } else if (!isAStore && isBStore) {
            return 1; // 倉庫は後に
          } else if (isAStore && isBStore) {
            return aName.localeCompare(bName, "ja"); // 店舗同士をあいうえお順に並び替え
          } else {
            return aName.localeCompare(bName, "ja"); // 倉庫同士をあいうえお順に並び替え
          }
        });
        const colorIds: string[] = [];
        this.skus.forEach((sku) => {
          const skuAlerts = this.skuAlerts.filter((skuAlert) => skuAlert.id === sku.id);
          if (colorIds.includes(sku.colorId)) {
            this.skuTableTabs
              .find((colorTab) => colorTab.colorId === sku.colorId)
              ?.sizes.push({
                skuId: sku.id,
                sizeId: sku.sizeId,
                skuAlerts: skuAlerts.map((skuAlert) => {
                  return {
                    id: skuAlert.id,
                    alertId: skuAlert.alertId,
                    alertMasterId: skuAlert.alertMasterId,
                    userDeleted: skuAlert.userDeleted,
                  };
                }),
              });
          } else {
            this.skuTableTabs.push({
              colorId: sku.colorId,
              sizes: [
                {
                  skuId: sku.id,
                  sizeId: sku.sizeId,
                  skuAlerts: skuAlerts.map((skuAlert) => {
                    return {
                      id: skuAlert.id,
                      alertId: skuAlert.alertId,
                      alertMasterId: skuAlert.alertMasterId,
                      userDeleted: skuAlert.userDeleted,
                    };
                  }),
                },
              ],
            });
            colorIds.push(sku.colorId);
          }
        });
        if (this.skuTableTabs[0]) {
          this.skuTableInfo = await RadialApiClient.getSkuDetailTrends(this.skuTableTabs[0].sizes[0].skuId);
          this.skuTableInfo?.items.sort((a, b) => {
            const aName = this.getChanelName(a.storeId, a.warehouseId);
            const bName = this.getChanelName(b.storeId, b.warehouseId);

            // 店舗側か倉庫側かを判定
            const isAStore = a.storeId !== null;
            const isBStore = b.storeId !== null;

            if (isAStore && !isBStore) {
              return -1; // 店舗を優先
            } else if (!isAStore && isBStore) {
              return 1; // 倉庫は後に
            } else if (isAStore && isBStore) {
              return aName.localeCompare(bName, "ja"); // 店舗同士をあいうえお順に並び替え
            } else {
              return aName.localeCompare(bName, "ja"); // 倉庫同士をあいうえお順に並び替え
            }
          });
          this.selectedSkuColor = this.skuTableTabs[0];
          this.selectedSkuSize = this.skuTableTabs[0].sizes[0];
        }
      } else {
        this.skuTableTabs = [];
        this.selectedSkuColor = null;
        this.selectedSkuSize = null;
      }
      this.isLoading = false;
    },
    getServiceLogo(storeId: string, warehouseId: string): string {
      const serviceId =
        warehouseId === null
          ? this.stores.find((store) => store.id === storeId)?.serviceId
          : this.warehouses.find((warehouse) => warehouse.id === warehouseId)?.serviceId;
      switch (serviceId) {
        case 0:
          return require("@/assets/smaregi-logo.svg");
        case 1:
          return require("@/assets/shopify.svg");
        case 100:
          return require("@/assets/futureshop.svg");
        case 101:
          return require("@/assets/shopify.svg");
        case 103:
          return require("@/assets/base.svg");
        case 200:
          return require("@/assets/logizard.png");
        default:
          return require("@/assets/customServiceLogo.svg");
      }
    },
    getChanelName(storeId: string | null, warehouseId: string | null): string {
      const chanelName =
        warehouseId === null
          ? this.stores.find((store) => store.id === storeId)?.name
          : this.warehouses.find((warehouse) => warehouse.id === warehouseId)?.name;
      return chanelName ?? "";
    },
    async changeColorTab(skuColor: SkuTrendTableTabs) {
      this.isLoading = true;
      this.selectedSkuColor = skuColor;
      this.selectedSkuSize = this.selectedSkuColor.sizes[0];
      // SKU切り替え時は年間売上の降順表示に戻す
      this.skuSortBy = ["unitPrice"];
      this.skuSortDesc = [true];
      this.skuTableInfo = await RadialApiClient.getSkuDetailTrends(this.selectedSkuSize.skuId);
      this.skuTableInfo?.items.sort((a, b) => {
        const aName = this.getChanelName(a.storeId, a.warehouseId);
        const bName = this.getChanelName(b.storeId, b.warehouseId);

        // 店舗側か倉庫側かを判定
        const isAStore = a.storeId !== null;
        const isBStore = b.storeId !== null;

        if (isAStore && !isBStore) {
          return -1; // 店舗を優先
        } else if (!isAStore && isBStore) {
          return 1; // 倉庫は後に
        } else if (isAStore && isBStore) {
          return aName.localeCompare(bName, "ja"); // 店舗同士をあいうえお順に並び替え
        } else {
          return aName.localeCompare(bName, "ja"); // 倉庫同士をあいうえお順に並び替え
        }
      });
      this.isLoading = false;
    },
    async changeSizeTab(skuSize: SkuSizeTrendTableData) {
      this.isLoading = true;
      this.selectedSkuSize = skuSize;
      // SKU切り替え時は年間売上の降順表示に戻す
      this.skuSortBy = ["unitPrice"];
      this.skuSortDesc = [true];
      this.skuTableInfo = await RadialApiClient.getSkuDetailTrends(this.selectedSkuSize.skuId);
      this.skuTableInfo?.items.sort((a, b) => {
        const aName = this.getChanelName(a.storeId, a.warehouseId);
        const bName = this.getChanelName(b.storeId, b.warehouseId);

        // 店舗側か倉庫側かを判定
        const isAStore = a.storeId !== null;
        const isBStore = b.storeId !== null;

        if (isAStore && !isBStore) {
          return -1; // 店舗を優先
        } else if (!isAStore && isBStore) {
          return 1; // 倉庫は後に
        } else if (isAStore && isBStore) {
          return aName.localeCompare(bName, "ja"); // 店舗同士をあいうえお順に並び替え
        } else {
          return aName.localeCompare(bName, "ja"); // 倉庫同士をあいうえお順に並び替え
        }
      });
      this.isLoading = false;
    },
    async showActions() {
      this.isLoading = true;
      this.showingAction = true;
      this.customActionMasterList = (await RadialApiClient.listCustomActionMasters()) ?? [];
      // actionの一覧を取得(含まれているもののみ取得)
      const actionIds = this.actionInfo.pending.saleActions
        .map((action) => action.actionId)
        .concat(this.actionInfo.pending.customActions.map((action) => action.actionId))
        .concat(
          this.actionInfo.pending.stockActions
            .map((action) => action.stockActionDetails.map((stockAction) => stockAction.actionId))
            .flat()
            .filter((item): item is NonNullable<typeof item> => item !== null)
        )
        .concat(
          this.actionInfo.completed.map((action) => action.saleActions.map((saleAction) => saleAction.actionId)).flat()
        )
        .concat(
          this.actionInfo.completed
            .map((action) => action.customActions.map((customAction) => customAction.actionId))
            .flat()
        )
        .concat(
          this.actionInfo.completed
            .map((action) =>
              action.stockActions
                .map((stockAction) =>
                  stockAction.stockActionDetails.map((stockActionDetail) => stockActionDetail.actionId)
                )
                .flat()
            )
            .flat()
            .filter((item): item is NonNullable<typeof item> => item !== null)
        );
      for (const actionId of actionIds) {
        const action = await RadialApiClient.getAction(actionId);
        if (action) {
          this.actionList.push(action);
        }
      }
      // customActionDetailの一覧を取得(含まれているもののみ取得)
      const customActionDetailIds = this.actionInfo.pending.customActions
        .map((customAction) => customAction.customActionId)
        .concat(
          this.actionInfo.completed
            .map((action) => action.customActions.map((customAction) => customAction.customActionId))
            .flat()
        );
      for (const customActionDetailId of customActionDetailIds) {
        const customActionDetail = await RadialApiClient.getCustomActionDetail(customActionDetailId);
        if (customActionDetail) {
          this.customActionDetailList.push(customActionDetail);
        }
      }
      // stockActionDetailでリクエストの一覧を取得(含まれているもののみ取得)
      const stockActionDetailIds = this.actionInfo.requests
        .map((request) =>
          request.stockActionDetails
            .map((stockActionDetail) =>
              stockActionDetail.stockFollowInfos.map((stockInfo) => stockInfo.stockActionDetailId)
            )
            .flat()
        )
        .concat(
          this.actionInfo.requests.map((request) =>
            request.stockActionDetails
              .map((stockActionDetail) =>
                stockActionDetail.stockMovingInfos.map((stockInfo) => stockInfo.stockActionDetailId)
              )
              .flat()
          )
        )
        .flat();
      for (const stockActionDetailId of stockActionDetailIds) {
        const stockActionDetail = await RadialApiClient.getStockActionDetail(stockActionDetailId);
        if (stockActionDetail) {
          this.stockActionDetailList.push(stockActionDetail);
        }
      }
      this.isLoading = false;
    },
    getCustomActionMasterName(customActionDetailId: string): string {
      const actionId = this.customActionDetailList.find(
        (customActionDetail) => customActionDetail.id === customActionDetailId
      )?.actionId;
      const customActionMasterId = this.actionList.find((action) => action.id === actionId)?.customActionMasterId;
      if (customActionMasterId) {
        return (
          this.customActionMasterList.find((customActionMaster) => customActionMaster.id === customActionMasterId)
            ?.name ?? ""
        );
      } else {
        return "";
      }
    },
    getActionName(actionId: string | null): string {
      if (actionId) {
        return this.actionList.find((action) => action.id === actionId)?.title ?? "";
      } else {
        return "";
      }
    },
    getStoreName(storeId: string): string {
      return this.stores.find((store) => store.id === storeId)?.name ?? "";
    },
    getWarehouseName(warehouseId: string): string {
      return this.warehouses.find((warehouse) => warehouse.id === warehouseId)?.name ?? "";
    },
    getIcon(actionId: string | null): string {
      if (actionId) {
        const action = this.actionList.find((action) => action.id === actionId);
        if (action) {
          return (
            this.adminUserDetails.find((adminUserDetail) => {
              if (adminUserDetail !== null && action.managerAdminUserId !== null) {
                return adminUserDetail.id === action.managerAdminUserId;
              } else {
                return false;
              }
            })?.icon ?? ""
          );
        } else {
          return "";
        }
      } else {
        return "";
      }
    },
    getExecuteDate(date: Date): string {
      return dayjs(date).format("YYYY-MM-DD");
    },
    openActionList(actionId: string | null) {
      if (actionId) {
        const routeData = this.$router.resolve({
          name: "AdminActionList",
          params: { actionId },
        });
        window.open(routeData.href, "_blank");
      } else {
        const routeData = this.$router.resolve({
          name: "AdminActionList",
          params: this.createdActionId ? { actionId: this.createdActionId } : undefined,
        });
        window.open(routeData.href, "_blank");
      }
    },
    getIconFromStockActionDetailId(stockActionDetailId: string): string | null {
      const stockActionDetail = this.stockActionDetailList.find(
        (stockActionDetail) => stockActionDetail.id === stockActionDetailId
      );
      if (stockActionDetail && stockActionDetail.creatorStoreId === null) {
        if (stockActionDetail.creatorAdminUserId !== null) {
          // adminUserIdがある場合
          return (
            this.adminUserDetails.find((adminUserDetail) => adminUserDetail.id === stockActionDetail.creatorAdminUserId)
              ?.icon ?? ""
          );
        } else {
          // companyの場合
          return store.state.company?.icon ?? "";
        }
      } else {
        // 店舗の場合
        return null;
      }
    },
    getIconFromAdminUserId(adminUserId: string): string {
      return this.adminUserDetails.find((adminUserDetail) => adminUserDetail.id === adminUserId)?.icon ?? "";
    },
    selectRequest(stockActionDetailId: string) {
      if (this.selectedStockActionDetailIds.includes(stockActionDetailId)) {
        // stockActionDetailIdが含まれていたら削除
        this.selectedStockActionDetailIds = this.selectedStockActionDetailIds.filter(
          (selectedStockActionDetailId) => selectedStockActionDetailId !== stockActionDetailId
        );
      } else {
        // stockActionDetailIdが含まれていなかったら追加
        this.selectedStockActionDetailIds.push(stockActionDetailId);
      }
    },
    async deleteActionRequest(stockActionDetailId: string) {
      if (!this.product) {
        return;
      }
      this.isLoading = true;
      await RadialApiClient.deleteStockActionDetail(stockActionDetailId);
      // get Action Info
      this.actionInfo = (await RadialApiClient.getProductDetailActionList(this.product.id)) ?? {
        requests: [],
        pending: { saleActions: [], customActions: [], stockActions: [] },
        completed: [],
      };
      this.isLoading = false;
    },
    addActionButtonClicked() {
      // アクションタイプが異なる場合はエラーハンドリング
      if (this.selectedActionType) {
        this.showingCreateAction = true;
      } else {
        this.showAlert(
          `異なるアクションタイプのリクエストが選択されています。同一のアクションタイプのリクエストを選択しアクションを作成してください。`,
          "error"
        );
      }
    },
    cancelCreateAction() {
      // 設定をリセット
      this.showingCreateAction = false;
      this.createActionListInfo.title = null;
      this.createActionListInfo.managerId = null;
      this.createActionListInfo.executedDate = null;
    },
    async createAction() {
      if (this.createActionListInfo.title && this.selectedActionType) {
        // Actionテーブルにレコードを追加
        const actionDto: CreateActionDto = {
          title: this.createActionListInfo.title ?? "",
          type: this.selectedActionType,
          customActionMasterId: null,
          creatorAdminUserId: store.state.user?.sub,
          managerAdminUserId: this.createActionListInfo.managerId,
          status: "PENDING",
          company: { connect: { id: store.state.company?.id } },
          executedDate: this.createActionListInfo.executedDate,
        };
        const action = await RadialApiClient.createAction(actionDto);
        const dto: ActionCreateDto = {
          stockActionDetailIds: this.selectedStockActionDetailIds,
          title: this.createActionListInfo.title,
          actionId: action?.id ?? "",
        };
        await RadialApiClient.createActionFromRequests(dto)
          .then(async () => {
            this.showAlert("アクションを作成しました。", "success");
            // get Action Info
            this.actionInfo = (await RadialApiClient.getProductDetailActionList(this.product.id)) ?? {
              requests: [],
              pending: { saleActions: [], customActions: [], stockActions: [] },
              completed: [],
            };
            if (action) {
              this.actionList.push(action);
            }
          })
          .catch(() => {
            this.showAlert("エラーが発生しております。\n時間を空けてからもう一度お試しください。", "error");
          });
      }
      // 設定をリセット
      this.createActionListInfo.title = null;
      this.createActionListInfo.managerId = null;
      this.createActionListInfo.executedDate = null;
      this.selectedStockActionDetailIds = [];
      this.showingCreateAction = false;
    },
    createActionButtonDisable(): boolean {
      return !(this.createActionListInfo.title && this.createActionListInfo.managerId);
    },
    showAlert(message: string, type: string) {
      this.alertMessage = message;
      this.alertType = type;
      this.showingAlert = true;
    },
    getActionId(orderedActionInfo: OrderedActionInfoEntity): string {
      if (orderedActionInfo.saleActions.length !== 0) {
        // セールアクションがある場合
        return orderedActionInfo.saleActions[0].actionId;
      } else if (orderedActionInfo.customActions.length !== 0) {
        // カスタムアクションがある場合
        return orderedActionInfo.customActions[0].actionId;
      } else {
        // ストックアクションがある場合
        return orderedActionInfo.stockActions[0].stockActionDetails[0].actionId ?? "";
      }
    },
    async clearEntries() {
      this.searchEntries.alertMasters = [];
      this.searchEntries.stores = [];
      await this.search();
    },
    formatMoney(v) {
      return (this.tableFormat === 1 ? "¥" : "") + Number(v).toLocaleString();
    },
  },
  async mounted() {
    this.isLoading = true;
    // 作成者と担当者のリスト情報を取得
    if (store.state.company) {
      this.adminUserDetails.push({
        id: store.state.company.id,
        icon: store.state.company.icon ?? "",
        name: store.state.company.name,
      });
    }
    const adminUsers: AdminUser[] = (await RadialApiClient.listAdminUsers()) ?? [];
    adminUsers.map((adminUser) => {
      if (adminUser.adminUserMasterId !== 3) {
        this.adminUserDetails.push({ id: adminUser.id, icon: adminUser.icon ?? "", name: adminUser.name });
      }
    });
    await Promise.all([
      (async () => {
        this.alertDatas = (await RadialApiClient.listAlertMasters()) ?? [];
      })(),
      (async () => {
        this.skus = (await RadialApiClient.listSkusByProduct(this.product.id)) ?? [];
        this.skus.sort((a, b) => {
          const aColorCode = this.colors.find((color) => color.id === a.colorId)?.code ?? 0;
          const bColorCode = this.colors.find((color) => color.id === b.colorId)?.code ?? 0;
          const aSizeCode = this.sizes.find((size) => size.id === a.sizeId)?.code ?? 0;
          const bSizeCode = this.sizes.find((size) => size.id === b.sizeId)?.code ?? 0;
          if (aColorCode > bColorCode) return 1;
          if (aColorCode < bColorCode) return -1;
          if (aSizeCode > bSizeCode) return 1;
          if (aSizeCode < bSizeCode) return -1;
          return 0;
        });
      })(),
      (async () => {
        const allProductAlerts = (await RadialApiClient.listAlertsByProduct(this.product.id)) ?? [];
        this.productAlerts = allProductAlerts
          .filter((alert) => alert.skuId === null)
          .map((productAlert) => {
            return {
              id: productAlert.productId,
              alertId: productAlert.id,
              alertMasterId: productAlert.alertMasterId,
              userDeleted: productAlert.userDeleted,
            };
          });
        this.skuAlerts = allProductAlerts
          .filter((alert) => alert.skuId !== null)
          .map((skuAlert) => {
            return {
              id: skuAlert.skuId ?? "",
              alertId: skuAlert.id,
              alertMasterId: skuAlert.alertMasterId,
              userDeleted: skuAlert.userDeleted,
            };
          });
      })(),
    ]);
    // mounted時に都度取得しないよう、親コンポーネントからデータ取得
    this.selectedPeriod = this.propPeriod;
    this.productDetailSalesDigestRateChartEntity = this.propGraph;
    if (this.productDetailSalesDigestRateChartEntity.productData) {
      this.skuDetailSalesDigestRateChartData = { ...this.productDetailSalesDigestRateChartEntity.skuData };
      this.xLabel = this.productDetailSalesDigestRateChartEntity.productData.data.map(
        (element) => `${dayjs(element.date).format("MM.DD")}`
      );
      if (this.tableFormat === 0) {
        this.yDataColumn = this.productDetailSalesDigestRateChartEntity.productData.data.map(
          (element) => element.salesQuantity ?? 0
        );
      } else {
        this.yDataColumn = this.productDetailSalesDigestRateChartEntity.productData.data.map(
          (element) => element.salesPrice ?? 0
        );
      }
      this.yDataLine = this.productDetailSalesDigestRateChartEntity.productData.data.map(
        (element) => Math.ceil(Number(element.digestRate)) ?? 0
      );
    }
    // 店舗フィルタ一覧を作成
    [...this.stores]
      .sort((a, b) => {
        return a.name.localeCompare(b.name, "ja");
      })
      .map((store) => {
        if (store.operating) {
          this.productFilterStoreInput.push({ id: store.id, name: store.name, code: null });
        }
      });
    // 非稼働店舗がある場合は「集計対象外」の項目を追加
    if (this.stores.some((store) => !store.operating)) {
      this.productFilterStoreInput.push({ id: -1, name: "集計対象外", code: null }); // 集計対象外のidとして他のidとかぶらないように-1を割り当てる
    }
    // 店舗アカウントでログインしている場合は自店舗をデフォルトで選択
    if (!this.isAdmin) {
      const ownStore = this.stores.find((store) => store.email === this.user?.email);
      const ownStoreInput = this.productFilterStoreInput.find((input) => input.id === ownStore?.id);
      if (ownStoreInput) {
        this.searchEntries.stores = [ownStoreInput];
      }
    }
    // get sku table data
    if (!this.product) return;
    const res = await RadialApiClient.getSkuColorSizeTableData(this.product.id, undefined, {
      alerts: [],
      stores: this.searchEntries.stores.map((store) => (typeof store.id === "string" ? store.id : "inoperative")),
    });
    if (res) {
      this.applyTableData(res);
    }
    // get product detail images
    const productDetailImages = await RadialApiClient.getProductDetailImages(this.product.id);
    if (productDetailImages) {
      this.productDetailImages = productDetailImages;
    }
    // get filter info
    const skuFilterInfo = await RadialApiClient.getSkuFilterInfo();
    if (skuFilterInfo) {
      this.skuFilterInfo = skuFilterInfo;
    }
    // get Action Info
    this.actionInfo = (await RadialApiClient.getProductDetailActionList(this.product.id)) ?? {
      requests: [],
      pending: { saleActions: [], customActions: [], stockActions: [] },
      completed: [],
    };
    this.isLoading = false;
  },
});
